import type {
  Cadence,
  WorkflowCreateInput,
  WorkflowType,
  WorkflowUpdateInput,
} from '@/client/services/api/graphql/gql/graphql';

import AdminAssignmentsService from '../../api/AdminAssignmentsService';
import type { AssignmentFormData } from '../../types/assignmentTypes';
import { AudienceType } from '@/client/services/api/graphql/gql/graphql';
import { setSubmissionContentType } from '../utils/setSubmissionContentType';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useAuthStore } from '@/client/services/state/authStore';
import { useFormContext } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useReadLocalStorage } from 'usehooks-ts';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

export const useCreateAndUpdateWorkflow = () => {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { role, is_manager: isManager } = user;
  const { setToast } = useToastStore();
  const navigate = useNavigate();
  const { watch } = useFormContext<AssignmentFormData>();
  const { setStepIndex } = useAdminAssignmentsStore();
  const learnAppContentAssignmentSelection = useReadLocalStorage<any[]>(
    'learn-app-assignment-content-selection',
  );

  const { audience, settings, id, formBoundType, enabled } = watch();

  const {
    assignmentOptions,
    conditionals,
    daysToComplete,
    dueFromHired,
    escalateBuddy,
    escalateManager,
    escalationContacts,
    name,
    nonReminderMessages,
    objectNumber,
    objectType,
    overdueMessages,
    partnerPermissions,
    reminderMessages,
    requireRecompletion,
    responses,
    shareOptions,
    triggerObject,
    type,
  } = settings;

  const payload: Omit<WorkflowCreateInput, 'enabled'> | WorkflowUpdateInput = {
    audience: audience.map((audienceItem) => ({
      dateRange: audienceItem.dateRange,
      days: audienceItem.days,
      id: audienceItem.id,
      matchingCriteria: audienceItem.matchingCriteria,
      type:
        audienceItem.type === AudienceType.PartnerType
          ? AudienceType.PartnerType
          : audienceItem.type === AudienceType.HiredDate
          ? AudienceType.HiredDate
          : audienceItem.segmentationType === 'group'
          ? AudienceType.Group
          : audienceItem.segmentationType === 'user'
          ? AudienceType.User
          : AudienceType.Segment,
    })),
    settings: {
      assignmentOptions,
      conditionals: conditionals.map((conditional) => ({
        id: conditional.id,
        type: conditional.type,
      })),
      daysToComplete,
      dueFromHired,
      escalateBuddy,
      escalateManager,
      escalationContacts: escalationContacts.map((contact) => contact.id),
      name,
      nonReminderMessages: nonReminderMessages.map((message) => message.id),
      objectNumber,
      objectType,
      overdueMessages: overdueMessages.map((message) => ({
        cadence: message.cadence as Cadence,
        message: message.message.id,
        totalMessages: message.totalMessages,
      })),
      partnerPermissions,
      reminderMessages: reminderMessages.map((reminder) => ({
        message: reminder.message.id,
        timeBeforeDue: reminder.timeBeforeDue,
      })),
      requireRecompletion,
      // @ts-expect-error For responses can we return a value from the ContentTypes enum instead of a string?
      responses: responses.map((response) => ({
        ...response,
        content: {
          id: response?.content?.id,
          contentType: setSubmissionContentType(response?.content?.contentType),
        },
      })),
      shareOptions,
      triggerObject: triggerObject?.id,
      type,
    },
  };

  interface CreateWorkflowMutationVariables {
    enabled: boolean;
  }

  const createWorkflowMutation = useMutation({
    mutationFn: ({ enabled }: CreateWorkflowMutationVariables) =>
      AdminAssignmentsService.createWorkflow({ ...payload, enabled } as WorkflowCreateInput),
    onSuccess: () => {
      setToast({
        show: true,
        status: 'success',
        title: t('admin.assignments.publish.success'),
      });

      setStepIndex(0);

      if (learnAppContentAssignmentSelection && learnAppContentAssignmentSelection.length > 0) {
        localStorage.removeItem('learn-app-assignment-content-selection');

        window.location.replace('/pvt/#/overview/content');

        return;
      }

      if (isManager && role !== 'admin') {
        window.location.replace('/pvt/#/manager');

        return;
      }

      navigate('/admin/assignments/overview');
    },
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('admin.assignments.publish.error'),
      }),
  });

  const updateWorkflowMutation = useMutation({
    mutationFn: () =>
      AdminAssignmentsService.updateWorkflow(id, { ...payload, enabled } as WorkflowUpdateInput),
    onSuccess: () => {
      setToast({
        show: true,
        status: 'success',
        title: t('admin.assignments.update.success'),
      });

      setStepIndex(0);

      if (isManager && role !== 'admin') {
        window.location.replace('/pvt/#/manager');
      } else {
        navigate(`/admin/assignments/details/${id}`);
      }
    },
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('admin.assignments.update.error'),
      }),
  });

  return { createWorkflowMutation, updateWorkflowMutation };
};
